import { React, useState, useEffect } from 'react';
import { Grid, Box, Paper, Stack, Typography, FormControl, InputLabel, Select, MenuItem, FormHelperText, Tabs, Tab } from '@mui/material';
import { HiOutlineCurrencyRupee } from "react-icons/hi2";
import axios from 'axios';
import IncomeTaxTable from '../table-data/IncomeTaxTable';
import TaxSummaryTable from '../table-data/TaxSummaryTable';
import '../../App.css';
import { useNavigate } from 'react-router-dom';
import Notification from '../Notification';
import { setLoaderMessage } from '../../utils/loaderManager';

// Custom component for required field label
const RequiredLabel = ({ label }) => (
  <span>
    {label}
    <span style={{ color: 'red', marginLeft: '3px' }}>*</span>
  </span>
);

// Generate financial year options (e.g., 2024-2025, 2025-2026)
const getFinancialYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const options = [];
  // Generate options for current year and next 2 years
  for (let i = 0; i < 3; i++) {
    const startYear = currentYear + i;
    const endYear = startYear + 1;
    options.push({
      id: i + 1,
      title: `${startYear}-${endYear}`
    });
  }
  return options;
};

const financialYearOptions = getFinancialYearOptions();

// Set initial values to default financial year
const initialFValues = {
  financial_year: "2026-2027"
}

// Helper function to convert Buffer objects to strings
const convertBufferToString = (bufferObj) => {
  if (!bufferObj) return '';
  if (typeof bufferObj === 'string') return bufferObj;
  if (bufferObj.type === 'Buffer' && Array.isArray(bufferObj.data)) {
    // Convert Buffer data array to string
    const uint8Array = new Uint8Array(bufferObj.data);
    // Try to decode as UTF-8
    try {
      return new TextDecoder('utf-8').decode(uint8Array);
    } catch (e) {
      // If UTF-8 decoding fails, try to convert bytes to string
      return String.fromCharCode.apply(null, bufferObj.data);
    }
  }
  return String(bufferObj);
};

// Helper function to process API response data
const processTableData = (data) => {
  if (!Array.isArray(data)) return [];
  return data.map(item => {
    const firstName = item.first_name ? convertBufferToString(item.first_name) : '';
    const lastName = item.last_name ? convertBufferToString(item.last_name) : '';
    return {
      ...item,
      first_name: firstName,
      last_name: lastName,
      // Ensure employee_id is available for display when names are missing
      displayName: (firstName && lastName) 
        ? `${firstName} ${lastName}`
        : item.employee_id ? `Employee ID: ${item.employee_id}` : 'N/A'
    };
  });
};

// TabPanel component for rendering tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`income-tax-tabpanel-${index}`}
      aria-labelledby={`income-tax-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box 
          sx={{ 
            pt: 3,
            pb: 2,
            animation: 'fadeIn 0.3s ease-in',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateY(10px)' },
              to: { opacity: 1, transform: 'translateY(0)' }
            }
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

function IncomeTax() {
  const [values, setValues] = useState(initialFValues);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [projectedMonthlyGrossData, setProjectedMonthlyGrossData] = useState([]);
  const [projectedMonthlyTaxData, setProjectedMonthlyTaxData] = useState([]);
  const [taxSummaryData, setTaxSummaryData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const navigate = useNavigate();

  // Fetch data when financial year or active tab changes
  useEffect(() => {
    if (values.financial_year) {
      if (activeTab === 0) {
        fetchProjectedMonthlyGross();
      } else if (activeTab === 1) {
        fetchTaxSummary();
      } else if (activeTab === 2) {
        fetchProjectedMonthlyTax();
      }
    }
  }, [values.financial_year, activeTab]);

  const fetchProjectedMonthlyGross = async () => {
    if (!values.financial_year) return;

    setIsLoading(true);
    setLoaderMessage('Loading projected monthly gross...');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/create-payroll/get-projected-monthly-gross/${values.financial_year}`,
        {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      // Handle both response structures: direct array or {success: true, data: [...]}
      let responseData = null;
      if (Array.isArray(response.data)) {
        responseData = response.data;
      } else if (response.data.success && response.data.data) {
        responseData = response.data.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        responseData = response.data.data;
      }

      if (responseData && Array.isArray(responseData)) {
        if (responseData.length > 0) {
          const processedData = processTableData(responseData);
          setProjectedMonthlyGrossData(processedData);
        } else {
          setProjectedMonthlyGrossData([]);
        }
      } else {
        setProjectedMonthlyGrossData([]);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error'
        });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        setProjectedMonthlyGrossData([]);
        setNotify({
          isOpen: true,
          message: err.response?.data?.message || 'Failed to fetch projected monthly gross data. Please try again.',
          type: 'error'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProjectedMonthlyTax = async () => {
    if (!values.financial_year) return;

    setIsLoading(true);
    setLoaderMessage('Loading projected monthly tax...');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/create-payroll/get-emp-monthly-tax/${values.financial_year}`,
        {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      // Handle both response structures: direct array or {success: true, data: [...]}
      let responseData = null;
      if (Array.isArray(response.data)) {
        responseData = response.data;
      } else if (response.data.success && response.data.data) {
        responseData = response.data.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        responseData = response.data.data;
      }

      if (responseData && Array.isArray(responseData)) {
        if (responseData.length > 0) {
          const processedData = processTableData(responseData);
          setProjectedMonthlyTaxData(processedData);
        } else {
          setProjectedMonthlyTaxData([]);
        }
      } else {
        setProjectedMonthlyTaxData([]);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error'
        });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        setProjectedMonthlyTaxData([]);
        setNotify({
          isOpen: true,
          message: err.response?.data?.message || 'Failed to fetch projected monthly tax data. Please try again.',
          type: 'error'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTaxSummary = async () => {
    if (!values.financial_year) return;

    setIsLoading(true);
    setLoaderMessage('Loading tax summary...');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/create-payroll/get-emp-tax-summary/${values.financial_year}`,
        {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      // Handle both response structures: direct array or {success: true, data: [...]}
      let responseData = null;
      if (Array.isArray(response.data)) {
        responseData = response.data;
      } else if (response.data.success && response.data.data) {
        responseData = response.data.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        responseData = response.data.data;
      }

      if (responseData && Array.isArray(responseData)) {
        if (responseData.length > 0) {
          const processedData = processTableData(responseData);
          setTaxSummaryData(processedData);
        } else {
          setTaxSummaryData([]);
        }
      } else {
        setTaxSummaryData([]);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error'
        });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        setTaxSummaryData([]);
        setNotify({
          isOpen: true,
          message: err.response?.data?.message || 'Failed to fetch tax summary data. Please try again.',
          type: 'error'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const validate = () => {
    let temp = {};
    temp.financial_year = values.financial_year.length !== 0 ? "" : "This field is required.";
    setErrors({
      ...temp
    });
    return Object.values(temp).every(x => x === "");
  }

  function handleInputChange(e) {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", margin: "10px", padding: "10px", fontWeight: "500px" }}>
      <Paper elevation={5} sx={{ width: '100%', maxWidth: '1400px' }}>
        <Grid className="form-title" container spacing={2}>
          <Grid item> 
            <Typography variant="h4" sx={{ textAlign: "center", margin: 5, display: "flex", alignItems: "center", gap: 1 , marginBottom: "8px",marginTop: "4px"}}>
              <HiOutlineCurrencyRupee size={40} />
              Income Tax
            </Typography> 
          </Grid>
        </Grid>
        <Box sx={{ margin: "20px", marginTop: '0px' }}>
          <Stack spacing={2}>
            {/* Financial Year and Tabs on same line */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                marginBottom: "-21px",
                mx: "10px"
              }}
            >
              {/* Financial Year Selector */}
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background: 'linear-gradient(to right, #f8f9fa 0%, #e9ecef 100%)',
                  border: '1px solid #dee2e6',
                  flexShrink: 0
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: '#495057',
                      minWidth: 'fit-content'
                    }}
                  >
                    Select Financial Year:
                  </Typography>
                  <FormControl
                    variant="outlined"
                    {...(errors.financial_year && { error: true })}
                    sx={{
                      minWidth: 280,
                      '& .MuiOutlinedInput-root': {
                        background: 'white',
                        borderRadius: 2,
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#0b418e',
                          }
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#0b418e',
                            borderWidth: 2
                          }
                        }
                      }
                    }}
                  >
                    <InputLabel>
                      <RequiredLabel label="Financial Year" />
                    </InputLabel>
                    <Select
                      label="Financial Year"
                      name="financial_year"
                      size="medium"
                      value={values.financial_year}
                      onChange={handleInputChange}
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="2025-2026">2025-2026</MenuItem>
                      {
                        financialYearOptions.map(
                          item => (
                            <MenuItem key={item.id} value={item.title}>
                              {item.title}
                            </MenuItem>
                          )
                        )
                      }
                    </Select>
                    {errors.financial_year && (
                      <FormHelperText sx={{ fontWeight: 500 }}>
                        {errors.financial_year}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Stack>
              </Paper>

              {/* Tabs - same line */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 280,
                  background: '#ffffff',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid #e9ecef',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              >
                <Box
                  sx={{
                    borderBottom: 2,
                    borderColor: 'divider',
                    background: '#f8f9fa',
                    px: 2
                  }}
                >
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    aria-label="income tax tabs"
                    sx={{
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        minHeight: 56,
                        color: '#6c757d',
                        '&:hover': {
                          color: '#0b418e',
                          background: 'rgba(11, 65, 142, 0.08)'
                        },
                        '&.Mui-selected': {
                          color: '#0b418e',
                          fontWeight: 700
                        }
                      },
                      '& .MuiTabs-indicator': {
                        height: 4,
                        borderRadius: '4px 4px 0 0',
                        background: 'linear-gradient(90deg, #0b418e 0%, #764ba2 100%)'
                      }
                    }}
                  >
                    <Tab label="Monthly Gross Salary" />
                    <Tab label="Tax Summary" />
                    <Tab label="Monthly Tax" />
                  </Tabs>
                </Box>
              </Box>
            </Box>

            {/* Tab content - full width below */}
            <Box
              sx={{
                width: '100%',
                margin: "10px",
                background: '#ffffff',
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid #e9ecef',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}
            >
              <TabPanel value={activeTab} index={0}>
                <IncomeTaxTable data={projectedMonthlyGrossData} isLoading={isLoading} totalColumnLabel="Total Gross" />
              </TabPanel>
              
              <TabPanel value={activeTab} index={1}>
                <TaxSummaryTable
                  data={taxSummaryData}
                  isLoading={isLoading}
                  financialYear={values.financial_year}
                  onOtherIncomeSaved={fetchTaxSummary}
                />
              </TabPanel>
              
              <TabPanel value={activeTab} index={2}>
                <IncomeTaxTable data={projectedMonthlyTaxData} isLoading={isLoading} totalColumnLabel="Total TDS" />
              </TabPanel>
            </Box>
          </Stack>
        </Box>
      </Paper>
      <Notification
        notify={notify}
        setNotify={setNotify}
      />
    </Box>
  )
}

export default IncomeTax

